import { useMutation, useQueryClient } from '@tanstack/react-query'
import { GetMentionsResponse } from '@omnea-app/comments/commentsFrontendClient'
import { CommentResponse, CommentsHttpClient } from '@omnea-app/comments/commentsHttpClient'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { billCommentsQueryKey, requestCommentsQueryKey } from './commentQueryKeys'
import { getMentionsQueryKey, mentionsQueryKey } from './useGetMentions'
import { entityCommentsQueryKey } from './useListComments'

export type MarkMentionsResolvedInput = {
  mentions: Array<{ mentionId: string; requestId: string | null }>
  isResolved: boolean
}

export type MentionsResponse = CommentResponse<'/mentions/mark-resolved', 'get'>

// Cache shape for the infinite mentions query (useGetMentions uses useInfiniteQuery).
type InfiniteMentions = { pages: GetMentionsResponse[]; pageParams: unknown[] }

export function useMarkMentionsResolved() {
  const queryClient = useQueryClient()
  const { getToken } = useAuthenticatedUser()

  return useMutation({
    mutationFn: async ({ isResolved, mentions }: MarkMentionsResolvedInput) => {
      const apiClient = new CommentsHttpClient({ authorization: await getToken() })
      const { data, error } = await apiClient.POST('/mentions/mark-resolved', {
        body: { isResolved, mentionIds: mentions.map((m) => m.mentionId) },
      })
      if (error) {
        throw new Error(error.suggestion)
      }
      return data
    },
    onSuccess: async () => {
      // Refetch all comment and mention queries to ensure consistency
      // Covers both resolved and unresolved mentions despite optimistic update
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [requestCommentsQueryKey] }),
        queryClient.invalidateQueries({ queryKey: [entityCommentsQueryKey] }),
        queryClient.invalidateQueries({ queryKey: [billCommentsQueryKey] }),
        queryClient.invalidateQueries({ queryKey: [mentionsQueryKey] }),
      ])
    },
    onMutate: async ({ isResolved, mentions }: MarkMentionsResolvedInput) => {
      const idsToMove = new Set(mentions.map((m) => m.mentionId))
      const requestIds = new Set(mentions.map((m) => m.requestId).filter((id): id is string => id !== null))

      const updateScope = (sourceKey: string[], targetKey: string[]) => {
        const sourceData = queryClient.getQueryData<InfiniteMentions>(sourceKey)
        const targetData = queryClient.getQueryData<InfiniteMentions>(targetKey)
        if (!sourceData) return

        const toMove = sourceData.pages.flatMap((page) => page.mentions ?? []).filter((m) => idsToMove.has(m.id))
        if (toMove.length === 0) return

        // totalCount is the same value on every page, so recompute it once and
        // apply uniformly rather than subtracting per page.
        const sourceTotal = Math.max(0, (sourceData.pages[0]?.totalCount ?? 0) - toMove.length)
        queryClient.setQueryData<InfiniteMentions>(sourceKey, {
          ...sourceData,
          pages: sourceData.pages.map((page) => ({
            mentions: (page.mentions ?? []).filter((m) => !idsToMove.has(m.id)),
            totalCount: sourceTotal,
          })),
        })

        if (targetData) {
          const moved = toMove.map((m) => ({ ...m, isResolved }))
          const targetTotal = (targetData.pages[0]?.totalCount ?? 0) + moved.length
          queryClient.setQueryData<InfiniteMentions>(targetKey, {
            ...targetData,
            pages: targetData.pages.map((page, index) => ({
              mentions:
                index === 0
                  ? [...moved, ...(page.mentions ?? [])].sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''))
                  : (page.mentions ?? []),
              totalCount: targetTotal,
            })),
          })
        }
      }

      // Update dashboard queries
      updateScope(getMentionsQueryKey(!isResolved), getMentionsQueryKey(isResolved))

      // Update request-scoped queries
      for (const requestId of requestIds.values()) {
        updateScope(getMentionsQueryKey(!isResolved, requestId), getMentionsQueryKey(isResolved, requestId))
      }
    },
  })
}
